import type { ReactNode } from 'react';
import { Skeleton } from 'antd';

/**
 * ProfileTab uchun skeleton — yangi layoutga moslangan:
 *   - Brand-aligned hero (radial gradient subtle hint)
 *   - Uppercase section caption + content card uslubi
 *   - 2-qatorli ServiceRow ko'rinishi
 *   - Calendar + til guruhi
 */
export default function ProfileTabSkeleton() {
  return (
    <div key="profile-skeleton" className="bl-animate-in">
      {/* Hero */}
      <div
        style={{
          borderRadius: 'var(--bl-radius-card, 20px)',
          background:
            'radial-gradient(at 100% 0%, color-mix(in srgb, var(--bl-accent, #3390ec) 35%, white) 0%, color-mix(in srgb, var(--bl-accent, #3390ec) 20%, white) 65%)',
          padding: '20px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          overflow: 'hidden',
        }}
      >
        <Skeleton.Avatar active size={64} shape="circle" />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <Skeleton.Input active size="small" style={{ width: 160, height: 18 }} />
          <Skeleton.Input active size="small" style={{ width: 200, height: 13 }} />
        </div>
        <Skeleton.Avatar active size={44} shape="square" />
      </div>

      {/* Xizmatlar */}
      <SectionSkeleton>
        {[0, 1, 2].map((i) => (
          <ServiceRowSkeleton key={i} />
        ))}
      </SectionSkeleton>

      {/* Bildirishnomalar */}
      <SectionSkeleton>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 4px',
              borderBottom:
                i < 2
                  ? '1px solid var(--bl-divider, rgba(16,24,40,0.06))'
                  : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flex: 1,
              }}
            >
              <Skeleton.Avatar active size={20} shape="square" />
              <Skeleton.Input
                active
                size="small"
                style={{ width: 160, height: 14 }}
              />
            </div>
            <Skeleton.Input
              active
              size="small"
              style={{ width: 44, height: 22, borderRadius: 999 }}
            />
          </div>
        ))}
      </SectionSkeleton>

      {/* Calendar */}
      <SectionSkeleton>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Skeleton.Avatar active size={32} shape="square" />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <Skeleton.Input active size="small" style={{ width: '90%', height: 12 }} />
            <Skeleton.Input active size="small" style={{ width: '70%', height: 12 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Skeleton.Button active style={{ width: 150, height: 40, borderRadius: 10 }} />
          <Skeleton.Button active style={{ width: 130, height: 40, borderRadius: 10 }} />
        </div>
      </SectionSkeleton>
    </div>
  );
}

function CaptionSkeleton() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 4px',
        marginBottom: 8,
      }}
    >
      <Skeleton.Input active size="small" style={{ width: 90, height: 11 }} />
      <Skeleton.Input active size="small" style={{ width: 60, height: 18, borderRadius: 999 }} />
    </div>
  );
}

function SectionSkeleton({ children }: { children: ReactNode }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <CaptionSkeleton />
      <div
        style={{
          borderRadius: 'var(--bl-radius-card, 16px)',
          background: 'var(--bl-surface, #fff)',
          border: '1px solid var(--bl-divider, rgba(16,24,40,0.06))',
          padding: '12px 14px',
          boxShadow: 'var(--bl-shadow-card, 0 4px 16px rgba(16,24,40,0.04))',
        }}
      >
        {children}
      </div>
    </section>
  );
}

function ServiceRowSkeleton() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 10px 12px 12px',
        background: 'var(--bl-surface-soft, rgba(16,24,40,0.04))',
        borderRadius: 12,
        border: '1px solid var(--bl-divider, rgba(16,24,40,0.06))',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          width: 3,
          alignSelf: 'stretch',
          borderRadius: 2,
          background: 'var(--bl-divider, rgba(16,24,40,0.08))',
        }}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <Skeleton.Input active size="small" style={{ width: 140, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 12 }} />
      </div>
      <Skeleton.Avatar active size={28} shape="square" />
      <Skeleton.Input active size="small" style={{ width: 44, height: 22, borderRadius: 999 }} />
    </div>
  );
}
